import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../../services/config.service';
import { InitService } from '../../services/init.service';
import { interval, lastValueFrom, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

@Component({
    selector: 'app-license-banner',
    templateUrl: './license-banner.component.html',
    styleUrls: ['./license-banner.component.scss'],
    standalone: false
})
export class LicenseBannerComponent implements OnInit, OnDestroy {
  bannerMessage: string | null = null;
  isTrialActive: boolean = false;
  isExpired: boolean = false;
  private initCheckSub: Subscription;
  private licenseChecked: boolean = false;

  // Configuration - update with your actual license purchase URL
  purchaseUrl: string = 'https://lit.ai/purchase-license';

  constructor(
    private http: HttpClient,
    private initService: InitService
  ) {}

  ngOnDestroy(): void {
    if (this.initCheckSub) {
      this.initCheckSub.unsubscribe();
    }
  }

  ngOnInit() {
    // Skip license checks in local mode - it's free forever
    if (ConfigService.localMode) {
      this.bannerMessage = null;
      this.isTrialActive = false;
      this.isExpired = false;
      return;
    }

    // Check if the app is initialized before attempting to fetch license status
    this.checkInitialization();
  }

  checkInitialization() {
    // Check initialization status every 5 seconds until initialized, then stop
    this.initCheckSub = interval(5000)
      .pipe(takeWhile(() => !this.licenseChecked)) // Stop once license has been checked
      .subscribe(async () => {
        try {
          const response = await this.initService.getAsync();
          if (response.initialized && !this.licenseChecked) {
            this.licenseChecked = true;
            this.fetchLicenseStatus();
          }
        } catch (error) {
          console.error('Error checking initialization status:', error);
        }
      });
  }

  async fetchLicenseStatus() {
    try {
      const response: any = await lastValueFrom(this.http.get(`${ConfigService.apiUrl}/init/trial`));
      
      if (response.licensed) {
        // Licensed user, no banner needed
        this.bannerMessage = null;
        this.isTrialActive = false;
        this.isExpired = false;
      } else if (response.active) {
        // Trial is active
        this.bannerMessage = `TRIAL VERSION: ${response.days_remaining} days remaining`;
        this.isTrialActive = true;
        this.isExpired = false;
      } else {
        // Trial expired
        this.bannerMessage = 'Your trial period has expired. Please purchase a license to continue using LIT Platform.';
        this.isTrialActive = false;
        this.isExpired = true;
      }
    } catch (error) {
      console.error('Error fetching license status:', error);
      // On error, don't show any banner
      this.bannerMessage = null;
      this.isTrialActive = false;
      this.isExpired = false;
    }
  }
  
  goToPurchasePage() {
    // Open the purchase URL in a new tab
    window.open(this.purchaseUrl, '_blank');
  }
}
